import axios from 'axios';
import logger from '@/utils/global-logger';
import { executeWithBackoff, resetBackoffState, shouldSkipRequest } from './circuit-breaker-manager';
import { getErrorLoggingBaseURL } from './error-logging-config';

const ERROR_LOGGING_ENDPOINT = '/log/error';
const ERROR_LOGGING_IDENTIFIER = 'error-logging';
const X_HP_LAYOUT = 'X-HP-Layout';
const HP_LAYOUT_IN_APP = 'IN_APP';

/** Single cached axios instance; replaced when baseURL changes to avoid unbounded cache growth. */
let cachedBaseURL = null;
let cachedInstance = null;

function getAxiosInstance(baseURL) {
  if (cachedBaseURL === baseURL && cachedInstance) {
    return cachedInstance;
  }
  cachedBaseURL = baseURL;
  cachedInstance = axios.create({ baseURL });
  return cachedInstance;
}

const errorLoggingConfig = {
  maxConsecutiveFailures: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  rateLimitWindowMs: 10000,
  maxRequestsPerWindow: 10,
  timeoutMs: 5000,
  identifier: ERROR_LOGGING_IDENTIFIER,
};

export function resetLoggingFailureState() {
  resetBackoffState(ERROR_LOGGING_IDENTIFIER);
}

/**
 * @param {import('./error-logging-types').LogErrorRequest} payload - error_code, stack_trace?, meta?, source?
 * @param {{ baseURL?: string, endpoint?: string, contentType?: string }} [options]
 * @returns {Promise<void>}
 */
export function logErrorWithBackoff(payload, options = {}) {
  const baseURL = options.baseURL ?? getErrorLoggingBaseURL();
  if (!baseURL) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Error Logger] No base URL configured for error logging (hp_error_logging_base_url).');
    }
    return Promise.resolve();
  }

  const endpoint = options.endpoint ?? ERROR_LOGGING_ENDPOINT;
  const contentType = options.contentType ?? 'application/json';

  const axiosInstance = getAxiosInstance(baseURL);

  return executeWithBackoff({
    config: errorLoggingConfig,
    requestFn: (signal) =>
      axiosInstance.post(endpoint, payload, {
        headers: {
          'Content-Type': contentType,
          [X_HP_LAYOUT]: HP_LAYOUT_IN_APP,
        },
        signal,
      }),
    onCircuitBreakerOpen: () => {
      logger.w(
        `[Error Logger] Circuit breaker activated after ${errorLoggingConfig.maxConsecutiveFailures} consecutive failures. Error logging disabled.`
      );
    },
  });
}

/**
 * Skip logging when the failed request was to the error-logging endpoint (avoid loop).
 * @param {import('axios').AxiosError} error
 * @returns {boolean}
 */
export function shouldSkipErrorLoggingForInterceptor(error) {
  const requestUrl = error.config?.url || '';
  return shouldSkipRequest({
    config: errorLoggingConfig,
    url: requestUrl,
    skipUrlCheck: (url) => url.includes(ERROR_LOGGING_ENDPOINT),
  });
}
